import { AsyncPipe, NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatMenu, MatMenuItem, MatMenuTrigger } from "@angular/material/menu";
import { MatDrawer, MatDrawerContainer, MatDrawerContent } from "@angular/material/sidenav";
import { RouterOutlet } from "@angular/router";
import { combineLatest, distinctUntilChanged, filter, map, Observable, shareReplay, startWith, Subscription, takeUntil } from "rxjs";

import { openConfirmDialog } from "../confirmation-dialog/confirmation-dialog.constant";

import { AutoLogoutService } from "@app/core/auto-logout/auto-logout.service";
import { AutoLogoutDialogComponent } from "@app/core/auto-logout/components/auto-logout-dialog/auto-logout-dialog.component";
import { openAutoLogoutDialog } from "@app/core/auto-logout/components/auto-logout-dialog/auto-logout-dialog.constant";
import { NAVBAR_ITEM } from "@app/core/constants/navigation.const";
import { NotificationsPanelComponent } from "@app/core/notifications/components/notifications-panel/notifications-panel.component";
import { NotificationsSidebarService } from "@app/core/notifications/services/notifications-sidebar.service";
import { PermissionService } from "@app/core/permission/services/permission.service";
import { AppConfigStoreService } from "@app/core/services/app-config-store.service";
import { AuthService } from "@app/core/services/auth.service";
import { DestroyService } from "@app/core/services/destroy.service";
import { SessionService } from "@app/core/services/session.service";
import { SessionKeepAliveService } from "@app/core/services/session-keep-alive.service";
import { NotificationHubService } from "@app/core/signalR/notification-hub.service";
import { AuthPageActions } from "@app/features/auth/store/auth.actions";
import {
  activeOrganization,
  selectPasswordExpiresInDays,
  selectUserPermissions,
  userOrganizations,
} from "@app/features/auth/store/auth.selectors";
import { OnboardingOverlayComponent } from "@app/features/onboarding/components/onboarding-overlay/onboarding-overlay.component";
import { AiChatWidgetComponent } from "@app/shared/ai-assistant/components/ai-chat-widget/ai-chat-widget.component";
import { BackgroundOperationIndicatorComponent } from "@app/shared/components/background-operation-indicator/background-operation-indicator.component";
import { BreadcrumbsComponent } from "@app/shared/components/breadcrumbs/breadcrumbs.component";
import { BreadcrumbService } from "@app/shared/components/breadcrumbs/services/breadcrumb.service";
import { NavbarComponent } from "@app/shared/components/navbar/navbar.component";
import { PasswordExpiryBannerComponent } from "@app/shared/components/password-expiry-banner/password-expiry-banner.component";
import { ENavbarItemType, TNavbarChild, TNavigation } from "@app/shared/components/under-ui-header/types/navbar-item.type";
import { UnderUiHeaderComponent } from "@app/shared/components/under-ui-header/under-ui-header.component";
import { environment } from "@env/environment";
import { Store } from "@ngrx/store";


const AUTO_LOGOUT_KEY = "AUTH.LOGIN.AUTO_LOGOUT";

const HOUR_MS = (60 * 60 * 60) * 1000;

type TShowingLogout = MatDialogRef<AutoLogoutDialogComponent, boolean>;

@Component({
  selector: "under-ui-layout",
  templateUrl: "layout.component.html",
  styleUrls: ["layout.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UnderUiHeaderComponent,
    RouterOutlet,
    BreadcrumbsComponent,
    AsyncPipe,
    NgClass,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDrawer,
    MatDrawerContainer,
    MatDrawerContent,
    NotificationsPanelComponent,
    OnboardingOverlayComponent,
    NavbarComponent,
    PasswordExpiryBannerComponent,
    AiChatWidgetComponent,
    BackgroundOperationIndicatorComponent,
  ],
  providers: [DestroyService],
  standalone: true,
})
export class LayoutComponent implements OnInit {
  private readonly permissionService = inject(PermissionService);
  private readonly appConfigStore = inject(AppConfigStoreService);

  readonly navbarItems$: Observable<TNavigation[]> = combineLatest([
    this.store.select(selectUserPermissions).pipe(distinctUntilChanged()),
    // startWith([]) so the (non-gated) sidebar shows immediately; module-gated
    // groups stay hidden until /api/app/config confirms the module is enabled.
    this.appConfigStore.enabledModules$.pipe(startWith([] as string[])),
  ]).pipe(
    map(([, enabledModules]) => this.filterNavItems(NAVBAR_ITEM, enabledModules)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  isSidebarCollapsed = signal<boolean>(false);

  breadcrumbsItems$ = toSignal(this.breadcrumbsService.breadcrumbs$);
  stateBreadcrumbs$ = this.breadcrumbsService.stateBreadcrumbs;
  isOpenNotification = this.notificationsSidebarService.isOpen;

  unreadMessageCount$ = this.notificationsSidebarService.unreadNotificationsCount$;

  currentOrganization$ = this.store.select(activeOrganization);
  readonly organisation$ = this.store.select(userOrganizations);

  readonly passwordExpiresInDays$ = this.store.select(selectPasswordExpiresInDays);
  readonly isPasswordExpiryBannerDismissed = signal(false);

  private startShowingToaster!: TShowingLogout | null;
  private readonly durationTime = environment.autoLogoutConfig.timeShowNotificationSec;

  private autoLogoutTimerSubscription!: Subscription;
  private autoLogoutTimerOutSubscription!: Subscription;

  constructor(
    private readonly store: Store,
    private readonly matDialog: MatDialog,
    private readonly authService: AuthService,
    private readonly destroy$: DestroyService,
    private readonly sessionService: SessionService,
    private readonly breadcrumbsService: BreadcrumbService,
    private readonly autoLogoutService: AutoLogoutService,
    private readonly sessionKeepAliveService: SessionKeepAliveService,
    private readonly notificationHubService: NotificationHubService,
    private readonly notificationsSidebarService: NotificationsSidebarService,
  ) {}

  ngOnInit(): void {
    this.notificationHubService.connection();

    this.listenActivityUser();
    this.sessionKeepAliveService.start();
    this.listenNotificationHub();

    this.startShowingToaster = null;
  }

  changeOrganisation(organizationId: string, isActive: boolean): void {
    if (!isActive) {
      openConfirmDialog(this.matDialog, {
        title: "CANCEL_DIALOG.CONFIRMATION_TITLE",
        message: "COMMON.CHANGE_ORGANIZATION_MESSAGE",
        confirmText: "BTN.CONFIRM",
        cancelText: "BTN.CANCEL",
      })
        .pipe(
          filter(confirmed => confirmed),
          takeUntil(this.destroy$),
        )
        .subscribe(() => {
          this.store.dispatch(AuthPageActions.changeOrganization({ organizationId }));
        });
    }
  }

  onToggleNotificationSidebar(): void {
    this.notificationsSidebarService.toggle();
  }

  onToggleSidebar(): void {
    this.isSidebarCollapsed.update(v => !v);
  }


  private filterNavItems(items: TNavigation[], enabledModules: string[]): TNavigation[] {
    return items
      .filter(item => this.hasItemPermission(item) && this.hasItemModule(item, enabledModules))
      .map(item => {
        if (item.type === ENavbarItemType.Group) {
          const filteredChildren = item.children.filter(child => {
            if (child.type === ENavbarItemType.Header) {
              return true;
            }
            return this.hasItemPermission(child) && this.hasItemModule(child, enabledModules);
          });
          return {
            ...item,
            children: this.stripEmptyHeaders(filteredChildren),
          };
        }
        return item;
      })
      .filter(item => item.type !== ENavbarItemType.Group || item.children.length > 0);
  }

  private stripEmptyHeaders(children: TNavbarChild[]): TNavbarChild[] {
    return children.filter((child, index) => {
      if (child.type !== ENavbarItemType.Header) {
        return true;
      }
      const next = children[index + 1];
      return !!next && next.type !== ENavbarItemType.Header;
    });
  }

  private hasItemPermission(item: TNavigation): boolean {
    return !item.permission?.length ||
      item.permission.every(p => this.permissionService.hasPermission(p));
  }

  /** A nav item tagged with a `module` is shown only when that plugin module is enabled for the tenant. */
  private hasItemModule(item: TNavigation, enabledModules: string[]): boolean {
    return !item.module || enabledModules.includes(item.module);
  }

  private listenActivityUser(): void {
    const stateTimeExpiration = this.onStartTimeExpiration();

    if (stateTimeExpiration) {
      this.autoLogoutService.doStartWatching();

      this.autoLogoutTimerSubscription = this.autoLogoutService.doTimerStart().subscribe(() => {
        if (!this.startShowingToaster) {
          this.startShowingToaster = openAutoLogoutDialog(this.matDialog, { durationTime: this.durationTime });

          this.startShowingToaster
            .afterClosed()
            .pipe(filter(resetTime => !!resetTime))
            .subscribe(() => {
              this.autoLogoutService.resetTimer();

              this.startShowingToaster = null;
            });
        }
      });

      this.autoLogoutTimerOutSubscription = this.autoLogoutService.doTimeOutEvent().subscribe(() => {
        this.matDialog.closeAll();
        this.onStopWatching();

        // ROR-6097: signing out goes through SessionService, which clears the store as well and lets
        // the navigation past canDeactivateGuard. The previous version navigated and then reloaded
        // unconditionally, so a form that answered the guard with "Cancel" cancelled the navigation
        // and got the reload anyway — losing the work the prompt had just promised to keep.
        this.sessionService.terminate(AUTO_LOGOUT_KEY);
      });
    }
  }

  private onStopWatching(): void {
    this.autoLogoutService.doStopWatching();

    this.autoLogoutTimerSubscription.unsubscribe();
    this.autoLogoutTimerOutSubscription.unsubscribe();
  }

  private onStartTimeExpiration(): boolean {
    const data = this.authService.getRefreshDataEx() && new Date(this.authService.getRefreshDataEx() as string);

    if (data && !Number.isNaN(data.getTime())) {
      return HOUR_MS >= data.getTime() - Date.now();
    }

    return false;
  }

  // я думаю, шо стоїт пушити в масив значенни
  private listenNotificationHub(): void {
    this.notificationHubService.bellNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.notificationsSidebarService.addNotificationsCount(1));
  }
}
